/**
 * A practitioner, as the Practitioners page edits it: a `Practitioner` that AKB
 * or Agent Scheduling wrote, or a team member's. Which schema each one uses is
 * `getClinicPractitionerSchema`. This file holds only data description, no
 * runtime logic.
 *
 * Specialty is not here: it is stored on the practitioner's `PractitionerRole`s,
 * and the page shows it read-only from them.
 */
import Stethoscope from "lucide-react/dist/esm/icons/stethoscope";

import type { ConfigSchema } from "@/types/clinic-configuration";
import {
  FHIR_EXTENSIONS,
  FHIR_IDENTIFIER_SYSTEMS,
} from "@/constants/fhir.constants";
import { ROUTES } from "@/constants/routes.constants";
import { practitionerTitleSuggestions } from "@/content/clinic-configuration";

/**
 * Needs for i18next-scanner
 */
// t('Doctors')
// t('Title')
// t('First name')
// t('Last name')
// t('Biography')
// t('How Kora introduces this provider to patients')
// t('Email')
// t('Doctor ID')
export const practitionerSchema = {
  fields: {
    title: {
      kind: "text",
      path: "name.0.prefix.0",
      title: "Title",
      presentation: { suggestions: practitionerTitleSuggestions },
      orientation: "horizontal",
      appearance: "standard",
    },
    firstName: {
      kind: "text",
      path: "name.0.given.0",
      title: "First name",
      /**
       * Kora drops a practitioner with no name part, and AKB's sheet always has
       * both names, so neither a create nor an edit may leave one empty.
       */
      required: true,
      orientation: "horizontal",
      appearance: "standard",
    },
    lastName: {
      kind: "text",
      path: "name.0.family",
      title: "Last name",
      required: true,
      orientation: "horizontal",
      appearance: "standard",
    },
    /**
     * Optional, as on `Location`'s phone. An AKB run replaces the whole
     * record, so it erases an email set here on an AKB practitioner.
     */
    email: {
      kind: "text",
      path: "telecom.value",
      match: { system: "email" },
      title: "Email",
      orientation: "horizontal",
      appearance: "standard",
    },
    identifier: {
      kind: "text",
      path: "identifier.value",
      match: { system: FHIR_IDENTIFIER_SYSTEMS.PRACTITIONER },
      title: "Doctor ID",
      readonly: true,
    },
  },
  extensions: {
    biography: {
      kind: "text",
      slug: FHIR_EXTENSIONS.PRACTITIONER_BIOGRAPHY,
      valueType: "string",
      title: "Biography",
      description: "How Kora introduces this provider to patients",
      layout: "stacked",
    },
  },
} satisfies ConfigSchema.Resource;

/**
 * A practitioner Agent Scheduling knows, AKB's too or not: the same fields
 * with the email read-only. The Calendars screen shows that email with the
 * practitioner's calendar connection, so the Practitioners page must not change
 * it.
 */
export const schedulingPractitionerSchema = {
  ...practitionerSchema,
  fields: {
    ...practitionerSchema.fields,
    email: { ...practitionerSchema.fields.email, readonly: true },
  },
} satisfies ConfigSchema.Resource;

/**
 * A team member's practitioner, with or without an AKB or Scheduling
 * identifier: the same fields with the email read-only. The email is the
 * member's login, stored on its Medplum `User`, and the `Practitioner`'s
 * copy must not drift from it.
 */
export const memberPractitionerSchema = {
  ...practitionerSchema,
  fields: {
    ...practitionerSchema.fields,
    email: { ...practitionerSchema.fields.email, readonly: true },
  },
} satisfies ConfigSchema.Resource;

export const practitionersPage = {
  to: ROUTES.CLINIC_PRACTITIONERS,
  label: "Doctors",
  icon: Stethoscope,
  count: "practitionerCount",
} satisfies ConfigSchema.Page;
